import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { tg } from '../telegram';
import { CameraCapture } from '../components/CameraCapture';
import { ConfirmView, ConfirmItem } from '../components/ui/ConfirmView';
import { SuccessView } from '../components/ui/SuccessView';
import { ItemDropdown } from '../components/ui/ItemDropdown';
import { UnitDropdown } from '../components/ui/UnitDropdown';
import { AddBalanceItemModal } from '../components/ui/AddBalanceItemModal';
import { useSubmitAction } from '../hooks/useSubmitAction';
import { useMasterData } from '../hooks/useMasterData';
import { InventoryBalanceItem } from '../types';
import { formatNumber, formatQty, formatVariance } from '../utils/format';
import toast from 'react-hot-toast';
import {
  Camera,
  Upload,
  Plus,
  Trash2,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  ClipboardList,
} from 'lucide-react';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

interface BalanceItemRow {
  ingredient_name: string;
  actual_quantity: number;
  unit: string;
  current_quantity?: number;
  note?: string;
}

export const InventoryBalanceScreen: React.FC<Props> = ({ onBack, onSuccess }) => {
  const { ingredients, rawIngredients, units } = useMasterData();
  const [step, setStep] = useState<'INPUT' | 'CONFIRM' | 'SUCCESS'>('INPUT');
  const [warehouse, setWarehouse] = useState<'STORE' | 'CENTRAL'>('STORE');
  const [items, setItems] = useState<BalanceItemRow[]>([]);
  const [note, setNote] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentBalances, setCurrentBalances] = useState<InventoryBalanceItem[]>([]);

  const loadData = async () => {
    try {
      const inv = await api.getInventory();
      setCurrentBalances(inv || []);
    } catch {}
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOcr = async (base64Image: string) => {
    setIsScanning(true);
    tg.haptic.impact('medium');
    const toastId = toast.loading('AI đang đọc phiếu kiểm kê kho...');

    try {
      const res = await api.scanInventoryBalanceOcr(base64Image);
      const targetWh = res.warehouse || warehouse;
      if (res.warehouse) {
        setWarehouse(res.warehouse);
      }
      if (res.items && res.items.length > 0) {
        // Kho Tổng tuyệt đối không có BTP -> Lọc bỏ BTP nếu phiếu kiểm kê Kho Tổng
        const validItems = targetWh === 'CENTRAL'
          ? res.items.filter((it) => {
              const matchMeta = ingredients.find(
                (ing) => ing.name.toLowerCase() === it.ingredient_name.toLowerCase()
              );
              return !matchMeta || matchMeta.type !== 'PREPARED';
            })
          : res.items;

        if (validItems.length < res.items.length) {
          toast('Đã lọc bỏ các món Bán thành phẩm vì Kho Tổng chỉ kiểm kê Nguyên liệu thô.', { icon: 'ℹ️' });
        }

        setItems(
          validItems.map((it) => {
            const currentMatch = currentBalances.find(
              (b) => b.warehouse === targetWh && b.ingredient_name === it.ingredient_name
            );
            return {
              ingredient_name: it.ingredient_name,
              actual_quantity: it.quantity,
              unit: it.unit || 'g',
              current_quantity: currentMatch ? currentMatch.quantity : 0,
            };
          })
        );
        toast.success(`Đã nhận diện ${validItems.length} món kiểm kê`, { id: toastId });
      } else {
        toast.error('Không tìm thấy dữ liệu kiểm kê trong ảnh', { id: toastId });
      }
    } catch (err: any) {
      toast.error(`Lỗi OCR: ${err.message}`, { id: toastId });
    } finally {
      setIsScanning(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      handleOcr(base64);
    };
    reader.readAsDataURL(file);
  };

  const addItem = () => {
    tg.haptic.selectionChanged();
    setIsAddModalOpen(true);
  };

  const handleAddItem = (newItem: {
    ingredient_name: string;
    actual_quantity: number;
    unit: string;
    current_quantity?: number;
  }) => {
    setItems((prev) => [...prev, newItem]);
    toast.success(`Đã thêm ${newItem.ingredient_name} vào danh sách kiểm kê`);
  };

  const removeItem = (idx: number) => {
    tg.haptic.selectionChanged();
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: keyof BalanceItemRow, val: any) => {
    setItems((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        if (field === 'ingredient_name') {
          const matchMaster = ingredients.find((m) => m.name === val);
          const matchCurrent = currentBalances.find(
            (b) => b.warehouse === warehouse && b.ingredient_name === val
          );
          return {
            ...row,
            ingredient_name: val,
            unit: matchMaster ? matchMaster.unit : row.unit,
            current_quantity: matchCurrent ? matchCurrent.quantity : 0,
          };
        }
        return { ...row, [field]: val };
      })
    );
  };

  // Submit action hook
  const { isSubmitting, isDisabled, execute: submitBalance } = useSubmitAction(
    async () => {
      const valid = items.filter((it) => it.ingredient_name.trim());
      await api.adjustInventoryBalance(
        warehouse,
        valid.map((v) => ({
          ingredient_name: v.ingredient_name,
          actual_quantity: v.actual_quantity,
          note: v.note,
        })),
        note
      );
    },
    {
      successMessage: 'Cân bằng tồn kho thành công!',
      onSuccess: () => {
        setStep('SUCCESS');
      },
    }
  );

  const goToConfirm = () => {
    const valid = items.filter((it) => it.ingredient_name.trim());
    if (valid.length === 0) {
      toast.error('Vui lòng chọn ít nhất 1 mặt hàng để cân bằng');
      return;
    }
    tg.haptic.impact('light');
    setStep('CONFIRM');
  };

  // 1. Success Step
  if (step === 'SUCCESS') {
    return (
      <SuccessView
        title="Cân Bằng Kho Thành Công!"
        message={`Đã cập nhật lại số lượng tồn kho thực tế cho ${items.filter((it) => it.ingredient_name.trim()).length} mặt hàng tại ${warehouse === 'STORE' ? 'Kho Cửa Hàng' : 'Kho Tổng'}.`}
        onDone={onSuccess}
      />
    );
  }

  // 2. Confirm Step
  if (step === 'CONFIRM') {
    const confirmItems: ConfirmItem[] = items
      .filter((it) => it.ingredient_name.trim())
      .map((it) => {
        const cur = it.current_quantity ?? 0;
        const diff = it.actual_quantity - cur;
        const diffSign = formatVariance(diff, it.unit);
        return {
          label: it.ingredient_name,
          value: formatQty(it.actual_quantity, it.unit),
          sub: `Hiện tại trên máy: ${formatQty(cur, it.unit)} (Lệch: ${diffSign})`,
          badge: warehouse === 'STORE' ? 'Quầy Bar' : 'Kho Tổng',
        };
      });

    return (
      <ConfirmView
        title="Xác Nhận Cân Bằng Kho"
        description={`Kiểm tra số đếm thực tế sẽ được ép làm tồn kho chính thức tại ${warehouse === 'STORE' ? 'Kho Cửa Hàng' : 'Kho Tổng'}:`}
        items={confirmItems}
        note={note}
        onConfirm={submitBalance}
        onBack={() => setStep('INPUT')}
        isSubmitting={isSubmitting}
        isDisabled={isDisabled}
        confirmText="Xác Nhận Cập Nhật Số Tồn"
      />
    );
  }

  // 3. Input Step
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} className="stepper-btn" title="Quay lại" style={{ width: 36, height: 36 }}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>Luồng 5: Cân Bằng Tồn Kho</h2>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Điều chỉnh số tồn khớp với thực tế kiểm kê
          </span>
        </div>
      </div>

      {/* Warehouse Selector */}
      <div className="card" style={{ padding: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Kho Cần Cân Bằng:</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
          <button
            type="button"
            className={`btn ${warehouse === 'STORE' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setWarehouse('STORE');
              setItems((prev) =>
                prev.map((row) => {
                  const match = currentBalances.find(
                    (b) => b.warehouse === 'STORE' && b.ingredient_name === row.ingredient_name
                  );
                  return { ...row, current_quantity: match ? match.quantity : 0 };
                })
              );
            }}
            style={{ fontSize: 13, minHeight: 40 }}
          >
            Quầy Bar (Cửa Hàng)
          </button>
          <button
            type="button"
            className={`btn ${warehouse === 'CENTRAL' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setWarehouse('CENTRAL');
              let filtered = false;
              setItems((prev) =>
                prev.map((row) => {
                  const matchMeta = ingredients.find(
                    (i) => i.name.toLowerCase() === row.ingredient_name.toLowerCase()
                  );
                  if (matchMeta && matchMeta.type === 'PREPARED') {
                    filtered = true;
                    return { ingredient_name: '', actual_quantity: 0, unit: 'g', current_quantity: 0 };
                  }
                  const match = currentBalances.find(
                    (b) => b.warehouse === 'CENTRAL' && b.ingredient_name === row.ingredient_name
                  );
                  return { ...row, current_quantity: match ? match.quantity : 0 };
                })
              );
              if (filtered) {
                toast('Đã xóa món Bán thành phẩm vì Kho Tổng chỉ kiểm kê Nguyên liệu thô.', { icon: 'ℹ️' });
              }
            }}
            style={{ fontSize: 13, minHeight: 40 }}
          >
            Kho Tổng
          </button>
        </div>
      </div>

      {/* OCR Scan Banner */}
      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <ClipboardList size={18} color="var(--primary-accent)" />
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Quét Phiếu Kiểm Kê Kho (OCR AI)</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            onClick={() => setIsCameraOpen(true)}
            disabled={isScanning}
            className="btn btn-primary"
            style={{ fontSize: 13, minHeight: 44 }}
          >
            <Camera size={16} /> Chụp Ảnh
          </button>

          <label
            className={`btn btn-secondary ${isScanning ? 'opacity-50' : ''}`}
            style={{ fontSize: 13, minHeight: 44, cursor: 'pointer', margin: 0 }}
          >
            <Upload size={16} /> Chọn File
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              disabled={isScanning}
              onChange={handleFileUpload}
            />
          </label>
        </div>

        {isScanning && (
          <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--primary-accent)', fontSize: 13 }}>
            <RefreshCw size={18} className="animate-spin" style={{ margin: '0 auto 6px' }} />
            Đang nhận diện phiếu kiểm kê bằng Gemini...
          </div>
        )}
      </div>

      {/* Items List */}
      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Danh Sách Kiểm Kê Thực Tế</h3>
          <button onClick={addItem} className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 8px', minHeight: 32 }}>
            <Plus size={14} /> Thêm món
          </button>
        </div>

        {items.length === 0 ? (
          <div
            style={{
              padding: '28px 16px',
              textAlign: 'center',
              backgroundColor: 'var(--bg-surface-subtle)',
              borderRadius: 'var(--radius-md)',
              border: '1px dashed var(--border-color)',
            }}
          >
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>
              Chưa có món nào trong phiếu kiểm kê.
            </div>
            <button
              type="button"
              onClick={() => {
                tg.haptic.impact('light');
                setIsAddModalOpen(true);
              }}
              className="btn btn-secondary"
              style={{ fontSize: 13, margin: '0 auto', display: 'inline-flex' }}
            >
              <Plus size={15} /> Thêm Món Cần Kiểm Kê
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px 48px 30px', gap: 6, padding: '0 11px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>
              <div>TÊN NGUYÊN LIỆU</div>
              <div style={{ textAlign: 'center' }}>SL ĐẾM TT</div>
              <div style={{ textAlign: 'center' }}>ĐVT</div>
              <div></div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 64px 48px 30px',
                    gap: 6,
                    alignItems: 'flex-start',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <ItemDropdown
                      value={row.ingredient_name}
                      items={warehouse === 'CENTRAL' ? rawIngredients : ingredients}
                      placeholder={warehouse === 'CENTRAL' ? "Chọn nguyên liệu thô..." : "Chọn nguyên liệu / BTP..."}
                      onChange={(selectedName, defaultUnit) => {
                        const curBalance = currentBalances.find(
                          (c) => c.warehouse === warehouse && c.ingredient_name.toLowerCase() === selectedName.toLowerCase()
                        );
                        setItems((prev) =>
                          prev.map((item, i) =>
                            i === idx
                              ? {
                                  ...item,
                                  ingredient_name: selectedName,
                                  unit: item.unit || curBalance?.unit || defaultUnit || 'g',
                                  current_quantity: curBalance ? curBalance.quantity : 0,
                                }
                              : item
                          )
                        );
                      }}
                    />
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4, paddingLeft: 2, lineHeight: 1.3 }}>
                      Trên máy: <span className="font-bold">{formatNumber(row.current_quantity)}</span> {row.unit}
                    </div>
                  </div>

                  <input
                    type="number"
                    className="input num"
                    placeholder="SL"
                    value={row.actual_quantity === 0 ? '' : row.actual_quantity}
                    onChange={(e) => updateItem(idx, 'actual_quantity', Number(e.target.value))}
                    onBlur={(e) => {
                      if (!e.target.value) updateItem(idx, 'actual_quantity', 0);
                    }}
                    style={{
                      textAlign: 'center',
                      fontWeight: 800,
                      fontSize: 14,
                      padding: '4px 2px',
                      minHeight: 38,
                      height: 38,
                      boxSizing: 'border-box',
                    }}
                  />

                  <UnitDropdown
                    value={row.unit}
                    units={units}
                    onChange={(selectedUnit) => updateItem(idx, 'unit', selectedUnit)}
                  />

                  <button
                    onClick={() => removeItem(idx)}
                    className="stepper-btn"
                    style={{
                      width: 30,
                      minHeight: 38,
                      height: 38,
                      color: 'var(--color-danger)',
                      justifySelf: 'center',
                      padding: 0,
                    }}
                    title="Xóa dòng"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Ghi chú cân bằng:</label>
          <input
            type="text"
            className="input"
            placeholder="Ví dụ: Kiểm kê định kỳ đầu tuần..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginTop: 4 }}
          />
        </div>
      </div>

      {/* Next to Confirmation Button */}
      <button
        onClick={goToConfirm}
        className="btn btn-primary btn-full"
        style={{ fontSize: 16, minHeight: 52 }}
      >
        <ArrowRight size={18} /> Xem & Xác Nhận Cân Bằng ({items.filter((it) => it.ingredient_name.trim()).length} Món)
      </button>

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={isCameraOpen}
        onCapture={(base64) => handleOcr(base64.split(',')[1] || base64)}
        onClose={() => setIsCameraOpen(false)}
      />

      {/* Add Balance Item Modal */}
      <AddBalanceItemModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        ingredients={warehouse === 'CENTRAL' ? rawIngredients : ingredients}
        currentBalances={currentBalances}
        warehouse={warehouse}
        units={units}
        onAdd={handleAddItem}
      />
    </div>
  );
};
